import type { SceneCanvasState } from "../scene/model";

/** Copy validated scene values, retaining immutable strings (including originals).
 * JSON round trips unnecessarily copy every byte of base64 media. */
export function copySceneValue<T>(value: T): T {
  if (Array.isArray(value)) return value.map(copySceneValue) as T;
  if (value && typeof value === "object")
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, copySceneValue(v)]),
    ) as T;
  return value;
}

function equal(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (!a || !b || typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const left = Object.keys(a),
    right = Object.keys(b);
  return (
    left.length === right.length &&
    left.every(
      (k) =>
        Object.hasOwn(b, k) &&
        equal(
          (a as Record<string, unknown>)[k],
          (b as Record<string, unknown>)[k],
        ),
    )
  );
}

/** Bounded native timeline. Media strings belong to live snapshots, not a
 * permanent pool. Discarding Redo/old entries releases their references. */
export class NativeHistory {
  private snapshots: SceneCanvasState[] = [];
  private cursor = -1;
  constructor(private readonly limit = 50) {
    if (!Number.isInteger(limit) || limit < 1)
      throw new Error("Некорректный лимит истории.");
  }
  get current() {
    return this.snapshots[this.cursor];
  }
  get length() {
    return this.cursor + 1;
  }
  get canRedo() {
    return this.cursor < this.snapshots.length - 1;
  }
  capture(state: SceneCanvasState) {
    return copySceneValue(state);
  }
  read(snapshot: SceneCanvasState) {
    return copySceneValue(snapshot);
  }
  commit(snapshot: SceneCanvasState) {
    if (equal(this.current, snapshot)) return false;
    this.snapshots.length = this.cursor + 1;
    this.snapshots.push(snapshot);
    if (this.snapshots.length > this.limit) this.snapshots.shift();
    this.cursor = this.snapshots.length - 1;
    return true;
  }
  undo() {
    if (this.length < 2) return undefined;
    this.cursor--;
    return this.current;
  }
  redo() {
    if (!this.canRedo) return undefined;
    this.cursor++;
    return this.current;
  }
  reset(state: SceneCanvasState) {
    this.snapshots = [this.capture(state)];
    this.cursor = 0;
  }
}
